import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Textarea } from '../ui/textarea';
import {
  Facebook,
  Twitter,
  Linkedin,
  Instagram,
  Sparkles,
  Copy,
  Check,
  Loader2,
  RefreshCw,
  ExternalLink
} from 'lucide-react';
import { aiAPI } from '../../services/api';

const SocialShareModal = ({ isOpen, onClose, campaignId, campaignTitle, campaignUrl }) => {
  const [posts, setPosts] = useState({});
  const [loading, setLoading] = useState({});
  const [copied, setCopied] = useState({});
  const [selectedTone, setSelectedTone] = useState('inspiring');
  const [activeTab, setActiveTab] = useState('twitter');

  // WhatsApp Icon Component
  const WhatsAppIcon = ({ className }) => (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  );

  const platforms = [
    { id: 'twitter', name: 'Twitter/X', icon: Twitter, color: 'bg-emerald-500', charLimit: 280 },
    { id: 'facebook', name: 'Facebook', icon: Facebook, color: 'bg-blue-600', charLimit: 500 },
    { id: 'whatsapp', name: 'WhatsApp', icon: WhatsAppIcon, color: 'bg-green-600', charLimit: 1000 },
    { id: 'instagram', name: 'Instagram', icon: Instagram, color: 'bg-gradient-to-r from-emerald-500 to-teal-500', charLimit: 2200 },
    { id: 'linkedin', name: 'LinkedIn', icon: Linkedin, color: 'bg-blue-700', charLimit: 700 }
  ];

  const tones = [
    { id: 'inspiring', name: 'Inspiring', emoji: '✨' },
    { id: 'urgent', name: 'Urgent', emoji: '🚨' },
    { id: 'emotional', name: 'Emotional', emoji: '💝' },
    { id: 'professional', name: 'Professional', emoji: '💼' }
  ];

  const generatePost = async (platform) => {
    setLoading(prev => ({ ...prev, [platform]: true }));
    try {
      const result = await aiAPI.generateSocialPost(campaignId, platform, selectedTone);
      setPosts(prev => ({
        ...prev,
        [platform]: {
          content: result.post_content,
          hashtags: result.hashtags || []
        }
      }));
    } catch (error) {
      console.error('Failed to generate post:', error);
    } finally {
      setLoading(prev => ({ ...prev, [platform]: false }));
    }
  };

  const generateAllPosts = async () => {
    // Set all loading states
    const loadingState = {};
    platforms.forEach(p => { loadingState[p.id] = true; });
    setLoading(loadingState);
    
    try {
      const result = await aiAPI.generateAllSocialPosts(campaignId);
      console.log('API Result:', result); // Debug log
      
      const newPosts = {};
      if (result && result.posts) {
        // Handle both direct response and nested data
        const postsData = result.posts;
        Object.keys(postsData).forEach(platform => {
          const postData = postsData[platform];
          if (postData && !postData.error) {
            newPosts[platform] = {
              content: postData.post_content || postData.content || '',
              hashtags: postData.hashtags || []
            };
          }
        });
        console.log('Parsed posts:', newPosts); // Debug log
      }
      setPosts(newPosts);
    } catch (error) {
      console.error('Failed to generate posts:', error);
    } finally {
      // Clear all loading states
      const clearedLoading = {};
      platforms.forEach(p => { clearedLoading[p.id] = false; });
      setLoading(clearedLoading);
    }
  };

  const copyToClipboard = async (platform) => {
    const post = posts[platform];
    if (!post) return;
    
    const fullText = `${post.content}\n\n${campaignUrl}\n\n${post.hashtags.map(h => `#${h}`).join(' ')}`;
    
    try {
      await navigator.clipboard.writeText(fullText);
      setCopied(prev => ({ ...prev, [platform]: true }));
      setTimeout(() => setCopied(prev => ({ ...prev, [platform]: false })), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const shareNatively = (platform) => {
    const post = posts[platform];
    if (!post) return;

    const text = encodeURIComponent(post.content);
    const url = encodeURIComponent(campaignUrl);
    const hashtags = post.hashtags.join(',');

    const shareUrls = {
      twitter: `https://twitter.com/intent/tweet?text=${text}&url=${url}&hashtags=${hashtags}`,
      facebook: `https://www.facebook.com/sharer.php?u=${url}`,
      whatsapp: `https://api.whatsapp.com/send?text=${text}%0A%0A${url}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`
    };

    if (shareUrls[platform]) {
      // Use window.open with popup parameters for better compatibility
      const width = 580;
      const height = 400;
      const left = (window.innerWidth - width) / 2;
      const top = (window.innerHeight - height) / 2;
      window.open(
        shareUrls[platform], 
        `${platform}-share`,
        `width=${width},height=${height},left=${left},top=${top},menubar=no,toolbar=no,resizable=yes,scrollbars=yes`
      );
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-background border-border">
        <DialogHeader>
          <DialogTitle className="text-foreground flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-emerald-400" />
            AI Social Media Generator
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Generate engaging posts for all platforms with one click
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Tone Selector */}
          <div className="flex flex-wrap gap-2">
            <span className="text-sm text-muted-foreground mr-2">Tone:</span>
            {tones.map(tone => (
              <button
                key={tone.id}
                onClick={() => setSelectedTone(tone.id)}
                className={`px-3 py-1 rounded-full text-sm transition-all ${
                  selectedTone === tone.id
                    ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/50'
                    : 'bg-card text-muted-foreground border border-border hover:border-border'
                }`}
              >
                {tone.emoji} {tone.name}
              </button>
            ))}
          </div>

          {/* Generate All Button */}
          <Button
            onClick={generateAllPosts}
            disabled={Object.values(loading).some(v => v)}
            className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
          >
            {Object.values(loading).some(v => v) ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
            ) : (
              <><Sparkles className="w-4 h-4 mr-2" /> Generate All Posts</>
            )}
          </Button>

          {/* Platform Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="w-full bg-card border border-border">
              {platforms.map(platform => (
                <TabsTrigger
                  key={platform.id}
                  value={platform.id}
                  className="flex-1 data-[state=active]:bg-secondary"
                >
                  <platform.icon className="w-4 h-4 mr-1" />
                  <span className="hidden sm:inline">{platform.name}</span>
                </TabsTrigger>
              ))}
            </TabsList>

            {platforms.map(platform => (
              <TabsContent key={platform.id} value={platform.id} className="mt-4">
                <div className="space-y-3">
                  {/* Generate button for individual platform */}
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => generatePost(platform.id)}
                      disabled={loading[platform.id]}
                      className="border-border"
                    >
                      {loading[platform.id] ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <><RefreshCw className="w-4 h-4 mr-1" /> Regenerate</>
                      )}
                    </Button>
                    <span className="text-xs text-muted-foreground flex items-center">
                      Max {platform.charLimit} chars
                    </span>
                  </div>

                  {/* Post Content */}
                  <div className="relative">
                    <Textarea
                      value={posts[platform.id]?.content || ''}
                      onChange={(e) => setPosts(prev => ({
                        ...prev,
                        [platform.id]: { ...prev[platform.id], content: e.target.value }
                      }))}
                      placeholder={loading[platform.id] ? 'Generating your post...' : 'Click "Generate" to create an AI post'}
                      className="min-h-[120px] bg-card border-border text-foreground resize-none"
                      disabled={loading[platform.id]}
                    />
                    {posts[platform.id]?.content && (
                      <span className={`absolute bottom-2 right-2 text-xs ${
                        posts[platform.id].content.length > platform.charLimit ? 'text-red-400' : 'text-muted-foreground'
                      }`}>
                        {posts[platform.id].content.length}/{platform.charLimit}
                      </span>
                    )}
                  </div>

                  {/* Hashtags */}
                  {posts[platform.id]?.hashtags?.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {posts[platform.id].hashtags.map((tag, i) => (
                        <span key={i} className="px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded-full text-xs">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Action Buttons */}
                  {posts[platform.id]?.content && (
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={() => copyToClipboard(platform.id)}
                        className="flex-1 border-border"
                      >
                        {copied[platform.id] ? (
                          <><Check className="w-4 h-4 mr-2 text-green-500" /> Copied!</>
                        ) : (
                          <><Copy className="w-4 h-4 mr-2" /> Copy to Clipboard</>
                        )}
                      </Button>
                      <Button
                        onClick={() => shareNatively(platform.id)}
                        className={`${platform.color} text-white`}
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Share on {platform.name}
                      </Button>
                    </div>
                  )}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SocialShareModal;
